import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import type { IanaTimeZoneName } from "@redotech/redo-common-model/timezone-list";
import { ianaTimeZoneNameSchema } from "@redotech/redo-common-model/timezone-list";
import { TrackingPageStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import type { TrackingPageScheduledEvent } from "@redotech/redo-flows-common/tracking-page-scheduled-event";
import { scheduledEventsOverlap } from "@redotech/redo-flows-common/tracking-page-scheduled-event";
import { useTranslation } from "@redotech/redo-i18n";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { RedoOrderTrackingRpcClientContext } from "@redotech/redo-merchant-app-common/order-tracking-rpc-client-provider";
import { Flex } from "@redotech/redo-web/flex";
import * as inspectorStyles from "@redotech/redo-web/inspector.module.css";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import type { InvalidFlowStep } from "..";
import { FrontendStepType, PartialWithRequiredType } from "..";
import { InvalidStepsList } from "../../components/invalid-steps-list";
import { useDelayedAutoFocus } from "../common/use-delayed-auto-focus";
import { ScheduledEventEditor } from "./scheduled-event-editor";
import type { PartialScheduledEvent } from "./scheduled-events-section";
import {
  isCompleteScheduledEvent,
  ScheduledEventsSection,
} from "./scheduled-events-section";
import type { TrackingPageOption } from "./tracking-page-picker";
import { TrackingPagePicker } from "./tracking-page-picker";

type Step = PartialWithRequiredType<TrackingPageStep>;

type InspectorView = { kind: "list" } | { kind: "edit"; eventId?: string };

const TrackingPageInspector = memo(function TrackingPageInspector({
  step,
  setStep,
  onClose,
  invalidSteps,
  setSelectedId,
  showValidationErrors,
  save,
}: {
  step: Step;
  setStep: (step: Step) => void;
  onClose?: () => void;
  invalidSteps?: InvalidFlowStep[];
  setSelectedId?: (id: string | undefined) => void;
  showValidationErrors?: boolean;
  save?: () => void;
}) {
  const { t } = useTranslation();
  const orderTrackingRpcClient = useRequiredContext(
    RedoOrderTrackingRpcClientContext,
  );
  const [view, setView] = useState<InspectorView>({ kind: "list" });

  // Windows are always displayed and authored in the viewer's own timezone;
  // the stored UTC instants never move, so every admin sees the same moment
  // expressed in their local time.
  const parsedTimezone = ianaTimeZoneNameSchema.safeParse(
    Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const timezone: IanaTimeZoneName = parsedTimezone.success
    ? parsedTimezone.data
    : "America/Denver";

  const otherInvalidSteps = invalidSteps?.filter((s) => s.id !== step.id) || [];
  const hasOtherInvalidSteps = !!otherInvalidSteps.length;
  const delayedAutoFocusInvalidSteps =
    useDelayedAutoFocus(hasOtherInvalidSteps);

  const trackingPagesLoad = useLoad(async () => {
    const trackingPages = await orderTrackingRpcClient.getTrackingPages({});
    return trackingPages.map((page) => ({
      _id: page._id,
      name: page.name || t("Untitled"),
    }));
  }, [orderTrackingRpcClient]);

  const [locallyCreatedPages, setLocallyCreatedPages] = useState<
    TrackingPageOption[]
  >([]);

  const allTrackingPages = useMemo<TrackingPageOption[] | undefined>(() => {
    if (!trackingPagesLoad.value) {
      return undefined;
    }
    const existingIds = new Set(trackingPagesLoad.value.map((p) => p._id));
    return [
      ...trackingPagesLoad.value,
      ...locallyCreatedPages.filter((p) => !existingIds.has(p._id)),
    ];
  }, [trackingPagesLoad.value, locallyCreatedPages]);

  const handlePageCreated = (page: TrackingPageOption) =>
    setLocallyCreatedPages((prev) => [...prev, page]);

  const previewOrderLoad = useLoad(async () => {
    const result = await orderTrackingRpcClient.getRecentOrders({ limit: 1 });
    return result.orders[0];
  }, [orderTrackingRpcClient]);

  if (trackingPagesLoad.pending) {
    return (
      <Flex align="center" grow={1} justify="center">
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  const events: PartialScheduledEvent[] = step.scheduledEvents ?? [];

  const upsertEvent = (event: TrackingPageScheduledEvent) => {
    const index = events.findIndex((e) => e.id === event.id);
    const scheduledEvents =
      index >= 0
        ? events.map((e, i) => (i === index ? event : e))
        : [...events, event];
    setStep({ ...step, scheduledEvents });
  };

  const removeEvent = (eventId: string) => {
    setStep({
      ...step,
      scheduledEvents: events.filter((e) => e.id !== eventId),
    });
  };

  if (view.kind === "edit") {
    const editedEvent = view.eventId
      ? events.find((e) => e.id === view.eventId)
      : undefined;
    const otherEvents = events
      .filter((e) => e.id !== view.eventId)
      .filter(isCompleteScheduledEvent);
    return (
      <ScheduledEventEditor
        event={editedEvent}
        onBack={() => setView({ kind: "list" })}
        onPageCreated={handlePageCreated}
        onSave={(event) => {
          upsertEvent(event);
          setView({ kind: "list" });
        }}
        otherEvents={otherEvents}
        previewOrder={previewOrderLoad.value}
        stepId={step.id}
        timezone={timezone}
        trackingPages={allTrackingPages}
      />
    );
  }

  return (
    <Flex dir="column" gap="2xl" pb="3xl">
      <Flex justify="space-between" py="sm">
        <Text fontSize="sm" fontWeight="semibold">
          {t("Tracking page")}
        </Text>
        {onClose && (
          <div className={inspectorStyles.closeButton} onClick={onClose}>
            <XCloseIcon height="16" width="16" />
          </div>
        )}
      </Flex>

      <TrackingPagePicker
        allowBuilderNavigation
        disabled={hasOtherInvalidSteps}
        label={t("Default")}
        onPageCreated={handlePageCreated}
        previewOrder={previewOrderLoad.value}
        replacementContent={
          hasOtherInvalidSteps && setSelectedId ? (
            <InvalidStepsList
              autoFocus={delayedAutoFocusInvalidSteps}
              invalidSteps={otherInvalidSteps}
              key={delayedAutoFocusInvalidSteps.toString()}
              onGoToStep={setSelectedId}
            />
          ) : undefined
        }
        save={save}
        setTrackingPageId={(trackingPageId) =>
          setStep({ ...step, trackingPageId })
        }
        showRequiredError={showValidationErrors && !step.trackingPageId}
        stepId={step.id}
        trackingPageId={step.trackingPageId}
        trackingPages={allTrackingPages}
      />
      {!step.trackingPageId && showValidationErrors && (
        <Text fontSize="xs" textColor="error">
          {hasOtherInvalidSteps
            ? t("Fix invalid steps before selecting a page", {
                count: otherInvalidSteps.length,
              })
            : t("Tracking page is required")}
        </Text>
      )}

      <ScheduledEventsSection
        disabled={hasOtherInvalidSteps}
        events={events}
        onCreateEvent={() => setView({ kind: "edit" })}
        onEditEvent={(eventId) => setView({ kind: "edit", eventId })}
        onRemoveEvent={removeEvent}
        showValidationErrors={showValidationErrors}
        trackingPages={trackingPagesLoad.value}
      />
    </Flex>
  );
});

export const trackingPageStepType: FrontendStepType<Step> = {
  type: "custom",
  valid: (step) => {
    if (!step.trackingPageId) {
      return false;
    }
    const events = step.scheduledEvents ?? [];
    if (!events.every(isCompleteScheduledEvent)) {
      return false;
    }
    return !scheduledEventsOverlap(events.filter(isCompleteScheduledEvent));
  },
  selectable: (_) => true,
  customInspector: (props) => (
    <TrackingPageInspector
      invalidSteps={props.invalidSteps}
      key={props.step.id}
      onClose={props.onClose}
      save={props.save}
      setSelectedId={props.setSelectedId}
      setStep={props.setStep}
      showValidationErrors={props.showValidationErrors}
      step={props.step}
    />
  ),
};
